import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { RotateCcw, Wand2 } from "lucide-react";
import { RevealLines } from "@/components/Reveal";
import { BraceletPreview } from "@/components/BraceletPreview";
import { useMakeBracelet } from "@/hooks/useMakeBracelet";
import { useCharm } from "@/hooks/useCharm";

const Question = ({ questions, step, onPick, a, prefix }) => (
  <motion.div key={`${step}-${questions[step][0]}`} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5 }} data-testid={`${prefix}-question-${step}`}>
    <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-amber-200">Question {step + 1} of {questions.length}</p>
    <div className="mt-3 h-1 rounded-full bg-purple-200/15 overflow-hidden">
      <div className={`h-full ${a.bar} transition-[width] duration-500`} style={{ width: `${(step / questions.length) * 100}%` }} />
    </div>
    <p className="mt-4 font-display text-3xl md:text-4xl leading-tight" data-testid={`${prefix}-question-text`}>{questions[step][0]}</p>
    <div className="mt-6 grid gap-3">
      {questions[step][1].map(([text], i) => (
        <button key={text} onClick={() => onPick(i)} data-testid={`${prefix}-answer-${step}-${i}`}
          className={`text-left rounded-2xl border ${a.answer} px-5 py-4 transition-colors ${a.hover}`}>
          {text}
        </button>
      ))}
    </div>
  </motion.div>
);

const Result = ({ r, onRestart, a, prefix, charm }) => {
  const make = useMakeBracelet();
  const showCharm = useCharm(charm);
  return (
    <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}
      className="grid sm:grid-cols-[180px_1fr] gap-6 items-center" data-testid={`${prefix}-result`}>
      <BraceletPreview beads={r.beads} className="max-w-[180px] mx-auto" />
      <div>
        <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-amber-200">You are</p>
        <p className="mt-2 font-display text-4xl md:text-5xl" data-testid={`${prefix}-result-name`}>{r.name}</p>
        <p className={`italic font-display text-xl mt-1 ${a.soft}`}>{r.title}</p>
        <p className="mt-4 text-purple-50/90 leading-relaxed">{r.text}</p>
        <div className="mt-6 flex flex-wrap gap-3">
          <button onClick={() => { make(r.beads, r.image); showCharm(); }} data-testid={`${prefix}-make-bracelet-btn`}
            className="inline-flex items-center gap-2 rounded-full bg-purple-200 text-[#0e0616] px-6 py-3 font-medium hover:bg-amber-200 transition-colors">
            <Wand2 className="w-4 h-4" /> Make their bracelet
          </button>
          <button onClick={onRestart} data-testid={`${prefix}-restart-btn`}
            className="inline-flex items-center gap-2 rounded-full border border-purple-200/40 px-6 py-3 hover:border-amber-200 hover:text-amber-200 transition-colors">
            <RotateCcw className="w-4 h-4" /> Try again
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export const FandomQuiz = ({ id, prefix, label, lines, intro, quiz, accent: a, charm, children }) => {
  const [questions, setQuestions] = useState(quiz.pickQuestions);
  const [answers, setAnswers] = useState([]);
  const done = answers.length === questions.length;
  const winner = useMemo(
    () => (done ? quiz.matchCharacter(answers.map((ans, q) => questions[q][1][ans][1])) : null),
    [done, answers, questions, quiz],
  );
  const restart = () => { setQuestions(quiz.pickQuestions()); setAnswers([]); };

  return (
    <section id={id} className="relative py-24 md:py-32 overflow-hidden" data-testid={`${prefix}-section`}>
      <div className="absolute inset-0 pointer-events-none" style={{ background: `radial-gradient(60% 50% at 80% 40%, ${a.glow}, transparent 70%)` }} />
      <div className="relative max-w-[1400px] mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-5">
          <p className={`font-mono text-[11px] uppercase tracking-[0.28em] mb-6 ${a.soft}`}>{label}</p>
          <h2 className="font-display text-5xl md:text-7xl leading-[0.92] tracking-tight">
            <RevealLines inView lines={[<>{lines[0]}</>, <em className={a.text}>{lines[1]}</em>]} />
          </h2>
          <p className="mt-6 max-w-md text-purple-50/90 leading-relaxed">
            {intro} Seven questions are picked fresh from {quiz.BANK_SIZE} every time, with {quiz.CHARACTERS.length} characters to match.
          </p>
          <p className="mt-4 text-xs text-purple-200/80">A fan-made quiz, not affiliated with the official series.</p>
        </div>
        <div className={`relative overflow-hidden lg:col-span-7 rounded-[28px] border ${a.border} ${a.panel} backdrop-blur-xl p-6 md:p-10 min-h-[420px]`}>
          {done && winner?.image && (
            <>
              <img src={winner.image} alt={winner.name} data-testid={`${prefix}-result-image`}
                className="absolute inset-0 w-full h-full object-cover object-top opacity-50" />
              <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/60 to-black/30" aria-hidden="true" />
            </>
          )}
          <div className="relative">
          {done
            ? <Result r={winner} onRestart={restart} a={a} prefix={prefix} charm={charm} />
            : <Question questions={questions} step={answers.length} onPick={(i) => setAnswers((x) => [...x, i])} a={a} prefix={prefix} />}
          </div>
        </div>
      </div>
      {children}
    </section>
  );
};
